import { IChecksModel } from '../models/check.model';
import * as CheckActions from './check.actions';
import { IAppState } from '../../store/app.state';
import { Action, createReducer, createSelector, on } from '@ngrx/store';

export type State = IChecksModel;

const initialState: State = {
  checks: {
    list: [],
    count: null,
    loader: null,
    checksSavedSuccessfully: null,
    query: {
      page: 0,
      pageSize: 10,
      sortField: 'index',
      sortDirection: 'ASC',
      query: ''
    }
  },
  loadingChecks: null,
  checksList: [],
  checksPropertyOptionsList: null,
  checksPropertySettings: [],
  checksLastSequence: 0
};

const checkReducer = createReducer(
  initialState,

  on(CheckActions.GetChecksList, (state, { payload }): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        list: [],
        query: payload
      },
      loadingChecks: true
    };
  }),
  on(CheckActions.GetChecksListSuccess, (state, { payload }): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        list: payload.result,
        count: payload.total
      },
      loadingChecks: false
    };
  }),

  on(CheckActions.GetChecks, (state): State => {
    return {
      ...state
    };
  }),

  on(CheckActions.GetChecksSuccess, (state, { payload }): State => {
    return {
      ...state,
      checksList: payload.checksList,
      checksPropertyOptionsList: payload.checksPropertyOptionsList,
      checksPropertySettings: payload.checksPropertySettings,
      checksLastSequence: payload.checksList
        .map(({ sequence }) => sequence)
        .sort(function (a, b) {
          return b - a;
        })[0]
    };
  }),

  on(CheckActions.GetChecksFailed, (state): State => {
    return {
      ...state,
      checksList: [],
      checksPropertyOptionsList: null,
      checksPropertySettings: [],
      checksLastSequence: 0
    };
  }),

  on(CheckActions.CreateCheck, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: true,
        checksSavedSuccessfully: null
      }
    };
  }),
  on(CheckActions.CreateCheckSuccess, (state, { payload }): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: true,
        list: [...state.checks.list, payload]
      },
      checksLastSequence: state.checksLastSequence + 1
    };
  }),
  on(CheckActions.CreateCheckFailed, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: null
      }
    };
  }),

  on(CheckActions.EditCheck, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: true,
        checksSavedSuccessfully: null
      }
    };
  }),
  on(CheckActions.EditCheckSuccess, (state, { payload }): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: true,
        list: state.checks.list.map((check) => (check._id === payload._id ? { ...check, ...payload } : check))
      }
    };
  }),
  on(CheckActions.EditCheckFailed, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: null
      }
    };
  }),

  on(CheckActions.DeleteCheck, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: true,
        checksSavedSuccessfully: null
      }
    };
  }),
  on(CheckActions.DeleteCheckSuccess, (state, { payload }): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: true,
        list: state.checks.list.filter((check) => check._id !== payload)
      }
    };
  }),
  on(CheckActions.DeleteCheckFailed, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        checksSavedSuccessfully: null
      }
    };
  }),

  on(CheckActions.ClearCheckSavedState, (state): State => {
    return {
      ...state,
      checks: {
        ...state.checks,
        checksSavedSuccessfully: null
      }
    };
  }),

  on(CheckActions.UpdateChecksPropertyOptionsList, (state, { payload }): State => {
    const obj = { ...state.checksPropertyOptionsList };
    obj[payload.property] = [payload.value, ...obj[payload.property]];
    return {
      ...state,
      checksPropertyOptionsList: obj
    };
  }),

  on(CheckActions.UpdateSelectCustomOptionsNewOption, (state, { payload }): State => {
    const currentCheck = state.checks.list.find((check) => check.internal === payload.internal);
    const updatedSelectCustomOptions = currentCheck[payload.property].map((option) => {
      if (option.description === payload.value) {
        return { ...option, selected: true };
      }
      return { ...option, selected: false };
    });
    const optionExists = updatedSelectCustomOptions.some((option) => option.description === payload.value);
    if (!optionExists) {
      updatedSelectCustomOptions.unshift({ description: payload.value, selected: false });
    }
    return {
      ...state,
      checks: {
        ...state.checks,
        loader: false,
        list: state.checks.list.map((check) =>
          check.internal === payload.internal ? { ...check, [payload.property]: updatedSelectCustomOptions } : check
        )
      }
    };
  })
);

const checksState = (state: IAppState) => state.checks;

export const reducer = (state: State, action: Action): State => checkReducer(state, action);

export const getChecksData = createSelector(checksState, (state: IChecksModel) => state.checks);

export const isLoadingChecks = createSelector(checksState, (state: IChecksModel) => state.loadingChecks);

export const getChecks = createSelector(checksState, (state: IChecksModel) => state.checksList);

export const getChecksLastSequence = createSelector(checksState, (state: IChecksModel) => state.checksLastSequence);

export const getChecksPropertyOptionsList = createSelector(
  checksState,
  (state: IChecksModel) => state.checksPropertyOptionsList
);

export const getChecksPropertySettings = createSelector(checksState, (state: IChecksModel) => state.checksPropertySettings);
